import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { PageHero } from "@/components/PageHero";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Check, ChevronDown, Loader2, Lock } from "lucide-react";

/**
 * Pricing — Founding Membership
 *
 * Master Spec: two-column layout with the primary pricing card on the LEFT
 * and the "why this matters" narrative on the RIGHT. Real Stripe Checkout
 * is live for $249 USD one-time. The live claimed counter reads from the
 * backend `/api/pricing/founding-status` endpoint (seeded to 1/250 by the
 * founder account, which is hidden from the public contributors wall).
 *
 * Confident, calm copy. No countdown, no fake urgency, no "hurry".
 */

const CHECKLIST = [
  "Full lifetime access to Wikificial as it grows",
  "Permanent Founding 250 status and badge",
  "Direct line to the people building it",
  "Vote on features, tools, and roadmap decisions",
  "Priority consideration for the Founding 250 Council",
  "Permanent Founding Member badge on your profile and every contribution you make",
  "Your name listed on the public Founding Contributors page, permanently",
  "Your price locked for life — never increases, even as the product grows",
  "Full access to the complete professional prompt library",
  "Private founding members channel with direct access to the team",
  "Unlimited Wikifi analyses and unlimited conversation history",
  "Early access to every new feature before public release",
];

const NARRATIVE = [
  {
    heading: "The next 5–10 years",
    body: (
      <>
        <p>
          You are at the beginning of the Artificial Intelligence Digital Revolution — one of the greatest shifts humanity will ever experience.
          AI will sit at the centre of it.
        </p>
        <p>
          A small number of companies already hold more market value than entire nations. Leading AI labs are valued in the hundreds of billions.
          Wikificial exists to build an AI system by people, for people.
        </p>
      </>
    ),
  },
  {
    heading: "What you get",
    body: (
      <>
        <p><strong>More than a membership. A seat at the origin.</strong></p>
        <p>
          You are not just an early user. You are a cornerstone member of a living community that will help shape and influence AI.
        </p>
        <ul>
          <li>Share real tools and workflows</li>
          <li>Give feedback that drives decisions</li>
          <li>Collaborate on ideas that matter</li>
          <li>Help guide the direction of the platform</li>
        </ul>
      </>
    ),
  },
  {
    heading: "The path forward",
    body: (
      <>
        <p>
          The most engaged and aligned members may be invited into the <strong>Founding 250 Council</strong>. Council members
          can test features early, join product discussions, help shape the roadmap, and guide the community.
        </p>
        <p>
          From there, some may move into larger roles: community leaders, advisors, creators, developers, designers, or founding team members.
          We look inside this group first.
        </p>
        <p>
          Selected long-term contributors may later receive equity opportunities or the chance to join future investment rounds (including SAFEs).
          It is our clear intention that the people who help build this from the start stay closest to what it becomes.
        </p>
      </>
    ),
  },
  {
    heading: "This is the line",
    body: (
      <>
        <p>
          Most people will simply use whatever the largest companies release. A very small group will help build the alternative.
        </p>
        <p>
          Only <strong>250 people</strong> will ever be able to say they were there at the beginning. When these spots are gone, they are gone for good.
        </p>
      </>
    ),
  },
];

const FAQ = [
  {
    q: "What exactly does founding status mean?",
    a: "A permanent badge on your profile and every contribution, a listing on the public Founding Contributors page, and lifetime access at your one-time price. You also get a direct vote on which tools and categories we test and add next.",
  },
  {
    q: "What happens when 250 seats are gone?",
    a: "Founding cannot be purchased once all 250 seats are taken. New joiners move to Creator at A$19/month, with none of the founding benefits.",
  },
  {
    q: "Is my access genuinely permanent?",
    a: "Yes. Founding is a one-time $249 USD purchase. There is no subscription, no renewal, no expiry. Your status on the account is permanent.",
  },
  {
    q: "What does the money fund?",
    a: "Independent tool testing, dual-scoring methodology, building Wikifi (the multi-model workspace), and keeping the directory free for everyone to browse. No ads inside the directory, no paid rankings.",
  },
  {
    q: "How is billing handled?",
    a: "All payments run through Stripe Checkout. You'll see US$249.00 charged as a one-time payment. Cards only for now — crypto is coming soon.",
  },
];

export default function Pricing() {
  const [seats, setSeats] = useState({ claimed: 0, cap: 250, remaining: 250 });
  const [loadingCheckout, setLoadingCheckout] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Poll real seat count so the counter is never stale.
  useEffect(() => {
    let stopped = false;
    const load = async () => {
      try {
        const r = await api.get("/pricing/founding-status");
        if (!stopped) setSeats(r.data);
      } catch (_) { /* non-fatal */ }
    };
    load();
    const t = setInterval(load, 30_000);
    return () => { stopped = true; clearInterval(t); };
  }, []);

  const soldOut = seats.remaining <= 0;
  const claimedPct = Math.min(100, Math.round((seats.claimed / seats.cap) * 100));

  const startCheckout = async () => {
    if (!user) { navigate("/login"); return; }
    setLoadingCheckout(true);
    try {
      const r = await api.post("/payments/checkout", {
        lookup_key: "founding_member_lifetime",
        origin_url: window.location.origin,
      });
      if (r.data.swapped_to_standard) {
        toast.info("Founding seats are full — routed you to the Creator plan instead.");
      }
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout. Please try again.");
      setLoadingCheckout(false);
    }
  };

  return (
    <Layout hideRightPanel>
      <PageHero
        testId="pricing-hero"
        variant="bare"
        eyebrow="Founding Membership"
        title="Own Wikificial. Forever."
        sub="Wikificial is being built with its earliest members. Founding members lock their price for life, shape what gets tested next, and stay named on the directory permanently."
      />

      {/* Live status bar — real counter from backend */}
      <div className="rounded-xl bg-white border border-[#E7EAF0] p-5 mb-8" data-testid="scarcity-bar">
        <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
          <div>
            <div className="wf-label mb-1">Founding 250</div>
            <div className="text-[20px] font-semibold text-[#0B1220] tabular-nums leading-none" data-testid="scarcity-count">
              {seats.claimed} <span className="text-[#9CA3AF]">/ {seats.cap}</span>{" "}
              <span className="text-[13px] text-[#6B7280] font-normal">claimed</span>
            </div>
          </div>
          <div
            className={`text-[13px] tabular-nums ${
              seats.remaining < 50 && seats.remaining > 0 ? "text-[#F97316] font-medium" : "text-[#6B7280]"
            }`}
            data-testid="scarcity-remaining"
          >
            {soldOut
              ? "All seats claimed"
              : seats.remaining < 50
                ? `Only ${seats.remaining} seats left`
                : `${seats.remaining} remaining`}
          </div>
        </div>
        <div
          className="h-1 rounded-full bg-[#F3F4F6] overflow-hidden"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={seats.cap}
          aria-valuenow={seats.claimed}
        >
          <div className="h-full bg-[#F97316] transition-[width] duration-500" style={{ width: `${claimedPct}%` }}/>
        </div>
      </div>

      {/* Two-column layout: LEFT = pricing card, RIGHT = narrative */}
      <div className="grid grid-cols-1 lg:grid-cols-[440px_1fr] gap-12 mb-16" data-testid="pricing-columns">
        {/* LEFT — Pricing card */}
        <div className="rounded-xl bg-white border border-[#E7EAF0] p-8 h-fit lg:sticky lg:top-6" data-testid="founding-card">
          <div className="wf-label mb-3" style={{ color: "#F97316" }}>Founding Member</div>
          <p className="text-[14px] text-[#6B7280] mb-5 leading-relaxed">
            Pay once. Lifetime access. Permanent status.
          </p>

          <div className="flex items-baseline gap-2 mb-6">
            <span className="text-[44px] font-bold text-[#0B1220] leading-none tracking-tight tabular-nums">$249</span>
            <span className="text-[14px] text-[#6B7280]">· one-time</span>
          </div>

          <ul className="space-y-2.5 mb-6" data-testid="founding-checklist">
            {CHECKLIST.map((f) => (
              <li key={f} className="flex items-start gap-2 text-[14px] text-[#0B1220] leading-relaxed">
                <Check size={15} className="mt-0.5 shrink-0 text-[#F97316]" strokeWidth={2.5}/>
                <span>{f}</span>
              </li>
            ))}
          </ul>

          <p className="text-[12px] text-[#6B7280] mb-4 leading-relaxed" data-testid="founding-permanence-note">
            Founding status is permanent. It cannot be purchased once all 250 seats are taken.
          </p>

          <button
            type="button"
            onClick={startCheckout}
            disabled={loadingCheckout || soldOut}
            className="btn-coral w-full"
            data-testid="purchase-founding-btn"
          >
            {loadingCheckout ? (
              <Loader2 size={15} className="animate-spin"/>
            ) : soldOut ? (
              <><Lock size={13}/> Founding closed</>
            ) : (
              "Purchase — $249"
            )}
          </button>

          {/* Payment method row — card active, crypto placeholder */}
          <div className="mt-4 flex items-center justify-between gap-2 text-[12px]">
            <span className="inline-flex items-center gap-1.5 text-[#0B1220]" data-testid="payment-card-available">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#10B981]" aria-hidden="true"/>
              Card payments available now
            </span>
            <span
              className="wf-pill bg-[#F7F8FA] border border-[#E7EAF0] text-[#6B7280]"
              data-testid="payment-crypto-soon"
            >
              Crypto — coming soon
            </span>
          </div>
        </div>

        {/* RIGHT — Narrative */}
        <div className="max-w-[620px]" data-testid="pricing-narrative">
          {NARRATIVE.map((section) => (
            <section key={section.heading} className="mb-10" data-testid={`narrative-${section.heading.replace(/\s+/g, "-").toLowerCase()}`}>
              <h2 className="wf-h2 mb-3">{section.heading}</h2>
              <div className="space-y-4 text-[15px] text-[#0B1220] leading-[1.7] founding-narrative">
                {section.body}
              </div>
            </section>
          ))}
        </div>
      </div>

      {/* FAQ */}
      <section className="max-w-[720px] mb-16" data-testid="pricing-faq">
        <h2 className="wf-h2 mb-4">Questions</h2>
        <div className="divide-y divide-[#E7EAF0] border-t border-b border-[#E7EAF0]">
          {FAQ.map((item, i) => <FaqItem key={i} question={item.q} answer={item.a}/>)}
        </div>
      </section>

      <p className="max-w-[720px] wf-meta pb-12" data-testid="pricing-legal">
        Founding membership provides access to the listed benefits. It does not represent ownership, shares, or an
        investment. Any future roles or equity opportunities are separate and not guaranteed.
      </p>

      {/* Narrative typography — mirrors .wikifi-prose but scoped so it only
          shapes the pricing-page copy. Bulleted lists use the accent dot. */}
      <style>{`
        .founding-narrative p { margin: 0; }
        .founding-narrative strong { font-weight: 600; color: #0B1220; }
        .founding-narrative ul {
          list-style: none;
          padding-left: 0;
          margin: 0;
          display: grid;
          gap: 8px;
        }
        .founding-narrative ul li {
          position: relative;
          padding-left: 16px;
          font-size: 15px;
          line-height: 1.55;
        }
        .founding-narrative ul li::before {
          content: "";
          position: absolute;
          left: 0;
          top: 10px;
          width: 5px;
          height: 5px;
          border-radius: 999px;
          background: #F97316;
        }
      `}</style>
    </Layout>
  );
}

function FaqItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <button
      type="button"
      onClick={() => setOpen((o) => !o)}
      className="w-full text-left py-4 flex flex-col gap-2"
      data-testid={`faq-${question.slice(0, 12).toLowerCase().replace(/\W+/g, "-")}`}
    >
      <div className="flex items-center justify-between gap-4">
        <span className="text-[15px] font-medium text-[#0B1220]">{question}</span>
        <ChevronDown size={15} className={`text-[#6B7280] shrink-0 transition-transform ${open ? "rotate-180" : ""}`}/>
      </div>
      {open && <p className="text-[14px] text-[#6B7280] max-w-[640px] leading-relaxed">{answer}</p>}
    </button>
  );
}
